import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, fmtDate, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Pill, TEMP_STYLES, STATUS_STYLES } from "@/components/badges";
import DateRangePicker from "@/components/DateRangePicker";
import { Plus, Search, X, Download, Trash2, Handshake, ArrowLeftRight } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const SOURCES = ["Self Generated", "Bark", "Firmable", "Partner", "Website", "Referral", "Networking Event", "Social Media", "Other"];
const TEMPS = ["Cold", "Warm", "Hot"];
const STATUSES = ["New", "Contacted", "Qualified", "Unqualified", "Converted"];

export default function Leads() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [services, setServices] = useState([]);
  const [partnerships, setPartnerships] = useState([]);
  const [filters, setFilters] = useState({
    q: searchParams.get("q") || "",
    source: searchParams.get("source") || "",
    temperature: searchParams.get("temperature") || "",
    status: searchParams.get("status") || "",
    assigned_to: searchParams.get("assigned_to") || "",
    partnership: searchParams.get("partnership") || "",
  });
  const [includeConverted, setIncludeConverted] = useState(searchParams.get("include_converted") === "1");
  const [dateRange, setDateRange] = useState({
    from: searchParams.get("date_from") || "",
    to: searchParams.get("date_to") || "",
  });
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [confirm, setConfirm] = useState(null); // null | {ids, mode}
  const [bulkReassign, setBulkReassign] = useState(false);
  const isAdmin = user?.role === "admin";

  const usersById = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);

  // Sync URL params on filter change
  useEffect(() => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => v && p.set(k, v));
    if (dateRange.from) p.set("date_from", dateRange.from);
    if (dateRange.to) p.set("date_to", dateRange.to);
    if (includeConverted) p.set("include_converted", "1");
    setSearchParams(p, { replace: true });
    // eslint-disable-next-line
  }, [filters.q, filters.source, filters.temperature, filters.status, filters.assigned_to, filters.partnership, dateRange.from, dateRange.to, includeConverted]);

  const load = async () => {
    setLoading(true);
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    if (dateRange.from) params.date_from = dateRange.from;
    if (dateRange.to) params.date_to = dateRange.to;
    if (includeConverted) params.include_converted = "true";
    const [l, u, sc, p] = await Promise.all([
      api.get("/leads", { params }),
      api.get("/users"),
      api.get("/service-categories"),
      api.get("/partnerships"),
    ]);
    setLeads(l.data); setUsers(u.data); setServices(sc.data); setPartnerships(p.data);
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters.q, filters.source, filters.temperature, filters.status, filters.assigned_to, filters.partnership, dateRange.from, dateRange.to, includeConverted]);

  const toggleOne = (id) => {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelected(s);
  };
  const toggleAll = () => {
    if (selected.size === leads.length) setSelected(new Set());
    else setSelected(new Set(leads.map((l) => l.id)));
  };
  const doDelete = async () => {
    if (!confirm) return;
    try {
      if (confirm.mode === "bulk") {
        const { data } = await api.post("/leads/bulk-delete", { ids: confirm.ids });
        toast.success(`${data.deleted} lead${data.deleted === 1 ? "" : "s"} deleted`);
      } else {
        await api.delete(`/leads/${confirm.ids[0]}`);
        toast.success("Lead deleted");
      }
      setSelected(new Set());
      setConfirm(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const exportCsv = async () => {
    const token = localStorage.getItem("eyb_token");
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/export/leads.csv`;
    const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` }, credentials: "include" });
    const blob = await r.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "leads.csv";
    a.click();
  };

  return (
    <div className="p-8" data-testid="leads-page">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="eyb-label">Pipeline</div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">Leads</h1>
          <p className="text-sm text-slate-500 mt-1">{leads.length} record{leads.length === 1 ? "" : "s"}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <DateRangePicker value={dateRange} onChange={setDateRange} defaultPreset="all" />
          <button data-testid="export-leads-btn" onClick={exportCsv} className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-slate-200 text-sm text-slate-700 hover:bg-slate-50">
            <Download className="w-4 h-4" /> Export CSV
          </button>
          <button data-testid="new-lead-btn" onClick={() => setShowNew(true)} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#29ABE2] hover:bg-[#1f8ebf] text-white text-sm font-semibold">
            <Plus className="w-4 h-4" /> New Lead
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="eyb-card p-4 mb-4">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              data-testid="leads-search"
              value={filters.q}
              onChange={(e) => setFilters({ ...filters, q: e.target.value })}
              placeholder="Search by name, business, email…"
              className="w-full pl-10 pr-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#29ABE2]/40"
            />
          </div>
          <select data-testid="filter-source" value={filters.source} onChange={(e) => setFilters({ ...filters, source: e.target.value })} className="px-3 py-2 border border-slate-200 rounded-md text-sm">
            <option value="">All sources</option>
            {SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select data-testid="filter-temperature" value={filters.temperature} onChange={(e) => setFilters({ ...filters, temperature: e.target.value })} className="px-3 py-2 border border-slate-200 rounded-md text-sm">
            <option value="">All temps</option>
            {TEMPS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select data-testid="filter-status" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className="px-3 py-2 border border-slate-200 rounded-md text-sm">
            <option value="">All statuses</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select data-testid="filter-assignee" value={filters.assigned_to} onChange={(e) => setFilters({ ...filters, assigned_to: e.target.value })} className="px-3 py-2 border border-slate-200 rounded-md text-sm">
            <option value="">All assignees</option>
            {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
          <select data-testid="filter-partnership" value={filters.partnership} onChange={(e) => setFilters({ ...filters, partnership: e.target.value })} className="px-3 py-2 border border-slate-200 rounded-md text-sm">
            <option value="">All partnerships</option>
            {partnerships.filter((p) => p.active).map((p) => <option key={p.id} value={p.name}>{p.name}</option>)}
          </select>
          <label className="inline-flex items-center gap-2 px-3 py-2 border border-slate-200 rounded-md text-sm bg-white cursor-pointer hover:bg-slate-50">
            <input
              type="checkbox"
              data-testid="filter-include-converted"
              checked={includeConverted}
              onChange={(e) => setIncludeConverted(e.target.checked)}
            />
            <span className="text-slate-700">Show converted</span>
          </label>
          {(filters.q || filters.source || filters.temperature || filters.status || filters.assigned_to || filters.partnership) && (
            <button onClick={() => setFilters({ q: "", source: "", temperature: "", status: "", assigned_to: "", partnership: "" })} className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
              <X className="w-3 h-3" /> Clear
            </button>
          )}
        </div>
      </div>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div className="eyb-card p-3 mb-3 flex items-center justify-between bg-[#29ABE2]/5 border-[#29ABE2]/30" data-testid="bulk-bar">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-slate-800">{selected.size} selected</span>
            <button onClick={() => setSelected(new Set())} className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
              <X className="w-3 h-3" /> Clear selection
            </button>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="bulk-reassign-btn"
              onClick={() => setBulkReassign(true)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#29ABE2] hover:bg-[#1f8ebf] text-white text-sm font-semibold"
            >
              <ArrowLeftRight className="w-4 h-4" /> Reassign to…
            </button>
            {isAdmin && (
              <button
                data-testid="bulk-delete-btn"
                onClick={() => setConfirm({ ids: Array.from(selected), mode: "bulk" })}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold"
              >
                <Trash2 className="w-4 h-4" /> Delete selected
              </button>
            )}
          </div>
        </div>
      )}

      <div className="eyb-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 bg-slate-50/60 border-b border-slate-200">
                <th className="px-3 py-3 w-10">
                  <input
                    data-testid="select-all-leads"
                    type="checkbox"
                    checked={leads.length > 0 && selected.size === leads.length}
                    onChange={toggleAll}
                    className="w-4 h-4 rounded accent-[#29ABE2] cursor-pointer"
                  />
                </th>
                <th className="px-4 py-3 font-semibold">Lead</th>
                <th className="px-4 py-3 font-semibold">Business</th>
                <th className="px-4 py-3 font-semibold">Source<div className="text-[9px] font-medium normal-case tracking-normal text-slate-400 mt-0.5">Partnership</div></th>
                <th className="px-4 py-3 font-semibold">Temp</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold">Assigned</th>
                <th className="px-4 py-3 font-semibold">Created</th>
                {isAdmin && <th className="px-4 py-3 w-10"></th>}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={isAdmin ? 9 : 8} className="py-6 text-center text-slate-400 text-xs">Loading…</td></tr>
              ) : leads.length === 0 ? (
                <tr><td colSpan={isAdmin ? 9 : 8} className="py-10 text-center text-slate-400 text-sm">No leads yet. Create one or import a CSV.</td></tr>
              ) : leads.map((l) => (
                <tr key={l.id} className="border-b border-slate-100 hover:bg-slate-50/50 group" data-testid={`lead-row-${l.id}`}>
                  <td className="px-3 py-3">
                    <input
                      data-testid={`select-lead-${l.id}`}
                      type="checkbox"
                      checked={selected.has(l.id)}
                      onChange={() => toggleOne(l.id)}
                      className="w-4 h-4 rounded accent-[#29ABE2] cursor-pointer"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <Link to={`/leads/${l.id}`} className="font-semibold text-slate-900 hover:text-[#1f8ebf]">{l.lead_name}</Link>
                    <div className="text-xs text-slate-500">{l.contact_person} · {l.email}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-700">{l.business_name || "—"}</td>
                  <td className="px-4 py-3">
                    <Pill className="bg-slate-100 text-slate-700 border border-slate-200">{l.source}</Pill>
                    {l.partnership && (
                      <div className="mt-1.5">
                        <Pill className="bg-[#29ABE2]/10 text-[#187199] border border-[#29ABE2]/30 text-[10px]">
                          <Handshake className="w-3 h-3 mr-1" /> {l.partnership}
                        </Pill>
                        {l.partner && (
                          <div className="text-[10px] text-slate-500 mt-0.5 ml-0.5 truncate max-w-[180px]" title={l.partner}>↳ {l.partner}</div>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3"><Pill className={TEMP_STYLES[l.temperature]}>{l.temperature}</Pill></td>
                  <td className="px-4 py-3"><Pill className={STATUS_STYLES[l.status] || "bg-slate-100 text-slate-700 border border-slate-200"}>{l.status}</Pill></td>
                  <td className="px-4 py-3 text-slate-700 text-xs">{usersById[l.assigned_to]?.name || "—"}</td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{fmtDate(l.created_at)}</td>
                  {isAdmin && (
                    <td className="px-4 py-3">
                      <button
                        data-testid={`delete-lead-${l.id}`}
                        onClick={() => setConfirm({ ids: [l.id], mode: "single" })}
                        className="opacity-0 group-hover:opacity-100 p-1.5 rounded text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-opacity"
                        title="Delete lead"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent data-testid="delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {confirm?.mode === "bulk" ? `${confirm.ids.length} leads` : "this lead"}?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the lead{confirm?.mode === "bulk" ? "s" : ""} and {confirm?.mode === "bulk" ? "their" : "its"} notes & audit history. This action cannot be undone. Opportunities already converted from these leads will not be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete" onClick={doDelete} className="bg-rose-600 hover:bg-rose-700 text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {showNew && <NewLeadModal users={users} services={services} partnerships={partnerships} onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); toast.success("Lead created"); }} />}

      {bulkReassign && (
        <BulkReassignModal
          ids={Array.from(selected)}
          users={users}
          onClose={() => setBulkReassign(false)}
          onDone={(result) => {
            setBulkReassign(false);
            setSelected(new Set());
            load();
            toast.success(`${result.transferred} lead${result.transferred === 1 ? "" : "s"} reassigned${result.skipped > 0 ? ` · ${result.skipped} skipped` : ""}`);
          }}
        />
      )}
    </div>
  );
}

function NewLeadModal({ onClose, onCreated, users, services, partnerships }) {
  const [form, setForm] = useState({
    lead_name: "", business_name: "", contact_person: "", email: "", phone: "", website: "",
    industry: "", location: "", source: "Self Generated", temperature: "Cold", status: "New",
    assigned_to: "", service_categories: [], partnership: "", partner: "", campaign_id: "",
  });
  const [partners, setPartners] = useState([]);
  const [campaigns, setCampaigns] = useState([]);

  useEffect(() => {
    if (!form.partnership) { setPartners([]); return; }
    api.get(`/partners?category=${encodeURIComponent(form.partnership)}`).then((r) => setPartners(r.data));
  }, [form.partnership]);
  useEffect(() => {
    api.get("/campaigns").then((r) => setCampaigns(r.data)).catch(() => setCampaigns([]));
  }, []);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/leads", { ...form, campaign_id: form.campaign_id || null });
      onCreated();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSaving(false); }
  };

  const set = (k, v) => setForm({ ...form, [k]: v });

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="new-lead-modal">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-heading text-lg font-semibold">New Lead</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Lead Name *" value={form.lead_name} onChange={(v) => set("lead_name", v)} required testid="field-lead-name" />
            <Field label="Business Name" value={form.business_name} onChange={(v) => set("business_name", v)} />
            <Field label="Contact Person" value={form.contact_person} onChange={(v) => set("contact_person", v)} />
            <Field label="Email" type="email" value={form.email} onChange={(v) => set("email", v)} />
            <Field label="Phone" value={form.phone} onChange={(v) => set("phone", v)} />
            <Field label="Website" value={form.website} onChange={(v) => set("website", v)} />
            <Field label="Industry" value={form.industry} onChange={(v) => set("industry", v)} />
            <Field label="Location" value={form.location} onChange={(v) => set("location", v)} />
            <Select label="Source" value={form.source} onChange={(v) => set("source", v)} options={SOURCES} />
            <Select label="Temperature" value={form.temperature} onChange={(v) => set("temperature", v)} options={TEMPS} />
            <Select label="Status" value={form.status} onChange={(v) => set("status", v)} options={STATUSES} />
            <Select label="Assign To" value={form.assigned_to} onChange={(v) => set("assigned_to", v)} options={[{ value: "", label: "— me —" }, ...users.map((u) => ({ value: u.id, label: u.name }))]} />
            <Select label="Partnership Category" value={form.partnership} onChange={(v) => setForm({ ...form, partnership: v, partner: "" })} options={[{ value: "", label: "— none —" }, ...partnerships.filter((p) => p.active).map((p) => ({ value: p.name, label: p.name }))]} />
            {form.partnership && (
              <Select
                label={`Partner Firm (${partners.length} in ${form.partnership})`}
                value={form.partner}
                onChange={(v) => set("partner", v)}
                options={[{ value: "", label: partners.length ? "— select a firm —" : "— no firms yet, manage in Partnerships —" }, ...partners.filter((p) => p.active).map((p) => ({ value: p.name, label: p.name }))]}
              />
            )}
            <Select
              label="Campaign"
              value={form.campaign_id}
              onChange={(v) => set("campaign_id", v)}
              options={[{ value: "", label: "— none —" }, ...campaigns.filter((c) => c.status !== "Ended").map((c) => ({ value: c.id, label: `${c.name}${c.status !== "Active" ? ` (${c.status})` : ""}` }))]}
            />
          </div>
          <div>
            <div className="eyb-label mb-2">Service Categories</div>
            <div className="flex flex-wrap gap-2">
              {services.filter((s) => s.active).map((s) => {
                const active = form.service_categories.includes(s.name);
                return (
                  <button type="button" key={s.id} onClick={() => set("service_categories", active ? form.service_categories.filter((x) => x !== s.name) : [...form.service_categories, s.name])}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${active ? "bg-[#29ABE2] text-white border-[#29ABE2]" : "bg-white text-slate-600 border-slate-200 hover:border-[#29ABE2]"}`}>
                    {s.name}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-md">Cancel</button>
            <button data-testid="submit-new-lead" type="submit" disabled={saving} className="px-4 py-2 bg-[#29ABE2] hover:bg-[#1f8ebf] text-white text-sm font-semibold rounded-full">{saving ? "Saving…" : "Create Lead"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testid }) {
  return (
    <label className="block">
      <span className="eyb-label">{label}</span>
      <input data-testid={testid} type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#29ABE2]/40" />
    </label>
  );
}

function Select({ label, value, onChange, options }) {
  return (
    <label className="block">
      <span className="eyb-label">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#29ABE2]/40">
        {options.map((o) => typeof o === "string" ? <option key={o} value={o}>{o}</option> : <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}

function BulkReassignModal({ ids, users, onClose, onDone }) {
  const [newOwner, setNewOwner] = useState("");
  const [notify, setNotify] = useState(true);
  const [saving, setSaving] = useState(false);
  const candidates = users.filter((u) => u.active !== false);
  const selectedUser = candidates.find((u) => u.id === newOwner);
  const submit = async (e) => {
    e.preventDefault();
    if (!newOwner) return;
    setSaving(true);
    try {
      const { data } = await api.post("/leads/bulk-transfer", { ids, assigned_to: newOwner, notify });
      onDone(data);
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="bulk-reassign-modal">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ArrowLeftRight className="w-4 h-4 text-[#29ABE2]" />
            <h3 className="font-heading text-lg font-semibold">Reassign {ids.length} lead{ids.length === 1 ? "" : "s"}</h3>
          </div>
          <button onClick={onClose}><X className="w-4 h-4 text-slate-400" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <label className="block">
            <span className="eyb-label">New owner</span>
            <select
              data-testid="bulk-owner-select"
              required
              value={newOwner}
              onChange={(e) => setNewOwner(e.target.value)}
              className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#29ABE2]/40"
            >
              <option value="">— select a user —</option>
              {candidates.map((u) => <option key={u.id} value={u.id}>{u.name} · {u.role.replace("_", " ")}</option>)}
            </select>
          </label>
          <label className="flex items-start gap-3 p-3 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50">
            <input
              data-testid="bulk-notify-checkbox"
              type="checkbox"
              checked={notify}
              onChange={(e) => setNotify(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-[#29ABE2]"
            />
            <div>
              <div className="text-sm font-semibold text-slate-800">Notify the new owner by email</div>
              <div className="text-xs text-slate-500 mt-0.5">
                {selectedUser ? <>One consolidated email to <span className="font-medium">{selectedUser.email}</span> with all {ids.length} leads</> : "A single summary email when checked"}
              </div>
            </div>
          </label>
          <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-md">Cancel</button>
            <button data-testid="submit-bulk-reassign" type="submit" disabled={saving || !newOwner} className="px-4 py-2 bg-[#29ABE2] hover:bg-[#1f8ebf] disabled:opacity-50 text-white text-sm font-semibold rounded-full">
              {saving ? "Reassigning…" : `Reassign ${ids.length}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

